import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLocationContext } from '../contexts/LocationContext';
import { getTeamMembers } from '../services/teamService';
import {
  getTimesheet,
  listTimeEntries,
  createTimeEntry,
  updateTimeEntry,
  deleteTimeEntry,
} from '../services/timeService';

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const fmtMoney = (v) => '$' + Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtHours = (v) => (Number(v || 0)).toFixed(2);

// Returns the Monday of the week containing the given Date.
const mondayOf = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  const day = x.getDay(); // 0=Sun..6=Sat
  const diff = day === 0 ? -6 : 1 - day;
  x.setDate(x.getDate() + diff);
  return x;
};

const fmtIsoDate = (d) => {
  const yr = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${yr}-${mo}-${da}`;
};

// Local datetime <-> input[datetime-local] string
const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const fromLocalInput = (str) => {
  if (!str) return null;
  const d = new Date(str);
  return isNaN(d.getTime()) ? null : d.toISOString();
};

const Timesheet = () => {
  const { membership, user } = useAuth();
  const { currentLocation } = useLocationContext();
  const isAdmin = user?.is_superadmin || membership?.role === 'owner' || membership?.role === 'admin';

  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const [timesheet, setTimesheet] = useState(null);
  const [entries, setEntries] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [formEntry, setFormEntry] = useState(null); // null = create; object = edit

  const weekStartStr = fmtIsoDate(weekStart);
  const weekEnd = useMemo(() => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + 6);
    return d;
  }, [weekStart]);

  const fetchAll = async () => {
    setLoading(true);
    setError('');
    try {
      const [tsData, entryData] = await Promise.all([
        getTimesheet(weekStartStr),
        listTimeEntries({ start: weekStartStr, end: fmtIsoDate(weekEnd) }),
      ]);
      setTimesheet(tsData);
      setEntries(entryData.entries || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load timesheet');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStartStr, currentLocation?.id]);

  useEffect(() => {
    if (!isAdmin) return;
    getTeamMembers().then((d) => setMembers((d.members || []).filter(m => m.active !== false)))
      .catch(() => {});
  }, [isAdmin]);

  const shiftWeek = (deltaDays) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + deltaDays);
    setWeekStart(mondayOf(d));
  };

  const totalLabor = useMemo(() => {
    if (!timesheet) return 0;
    return (timesheet.rows || []).reduce((s, r) => s + (r.labor_cost || 0), 0);
  }, [timesheet]);

  const totalHours = useMemo(() => {
    if (!timesheet) return 0;
    return (timesheet.rows || []).reduce((s, r) => s + (r.total_hours || 0), 0);
  }, [timesheet]);

  const openCreate = () => {
    setFormEntry({
      account_member_id: members[0]?.id || '',
      clock_in: '',
      clock_out: '',
      note: '',
    });
    setShowForm(true);
  };

  const openEdit = (entry) => {
    setFormEntry({
      id: entry.id,
      account_member_id: entry.account_member_id,
      clock_in: toLocalInput(entry.clock_in),
      clock_out: toLocalInput(entry.clock_out),
      note: entry.note || '',
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setFormEntry(null);
  };

  const saveForm = async () => {
    if (!formEntry?.account_member_id || !formEntry?.clock_in) {
      setError('Employee and clock-in time are required');
      return;
    }
    const payload = {
      account_member_id: Number(formEntry.account_member_id),
      clock_in: fromLocalInput(formEntry.clock_in),
      clock_out: formEntry.clock_out ? fromLocalInput(formEntry.clock_out) : null,
      note: formEntry.note,
    };
    try {
      if (formEntry.id) {
        await updateTimeEntry(formEntry.id, payload);
      } else {
        await createTimeEntry(payload);
      }
      closeForm();
      fetchAll();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save entry');
    }
  };

  const handleDelete = async (entry) => {
    if (!window.confirm('Delete this time entry?')) return;
    try {
      await deleteTimeEntry(entry.id);
      fetchAll();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete');
    }
  };

  if (loading && !timesheet) {
    return <div className="text-center py-12 text-gray-500">Loading timesheet...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h1 className="text-3xl font-bold text-gray-900">Timesheet</h1>
        {isAdmin && (
          <button
            onClick={openCreate}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
          >
            + Add manual entry
          </button>
        )}
      </div>

      {/* Week navigator */}
      <div className="bg-white rounded-lg shadow p-4 flex items-center justify-between gap-3">
        <button
          onClick={() => shiftWeek(-7)}
          className="text-sm text-gray-600 hover:text-gray-900 px-3 py-1 rounded hover:bg-gray-100"
        >
          ← Previous
        </button>
        <div className="text-sm font-medium text-gray-900 text-center">
          Week of {weekStart.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
          {' – '}
          {weekEnd.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
        </div>
        <button
          onClick={() => shiftWeek(7)}
          className="text-sm text-gray-600 hover:text-gray-900 px-3 py-1 rounded hover:bg-gray-100"
        >
          Next →
        </button>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">{error}</div>}

      {/* Summary tiles */}
      {isAdmin && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Tile label="Total hours" value={fmtHours(totalHours)} />
          <Tile label="Labor cost" value={fmtMoney(totalLabor)} />
          <Tile label="Active staff" value={(timesheet?.rows || []).length} />
          <Tile label="OT hours" value={fmtHours((timesheet?.rows || []).reduce((s, r) => s + (r.ot_hours || 0), 0))} />
        </div>
      )}

      {/* Weekly grid */}
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left px-3 py-2 font-medium text-gray-700">Employee</th>
              {DAY_LABELS.map((d, i) => {
                const dayDate = new Date(weekStart);
                dayDate.setDate(dayDate.getDate() + i);
                return (
                  <th key={d} className="text-right px-2 py-2 font-medium text-gray-700">
                    <div>{d}</div>
                    <div className="text-xs text-gray-400 font-normal">{dayDate.getDate()}</div>
                  </th>
                );
              })}
              <th className="text-right px-3 py-2 font-medium text-gray-700">Total</th>
              <th className="text-right px-3 py-2 font-medium text-gray-700">OT</th>
              {isAdmin && <th className="text-right px-3 py-2 font-medium text-gray-700">Labor</th>}
            </tr>
          </thead>
          <tbody>
            {(timesheet?.rows || []).length === 0 ? (
              <tr>
                <td colSpan={DAY_LABELS.length + (isAdmin ? 4 : 3)} className="text-center text-gray-500 py-6">
                  No entries this week.
                </td>
              </tr>
            ) : (
              (timesheet?.rows || []).map((row) => (
                <tr key={row.account_member_id} className="border-b border-gray-100">
                  <td className="px-3 py-2">
                    <div className="font-medium text-gray-900">{row.display_name || 'Unnamed'}</div>
                    {row.position && <div className="text-xs text-gray-500">{row.position}</div>}
                  </td>
                  {row.days.map((h, i) => (
                    <td key={i} className={`text-right px-2 py-2 tabular-nums ${h ? 'text-gray-900' : 'text-gray-300'}`}>
                      {h ? fmtHours(h) : '–'}
                    </td>
                  ))}
                  <td className="text-right px-3 py-2 font-semibold text-gray-900 tabular-nums">{fmtHours(row.total_hours)}</td>
                  <td className="text-right px-3 py-2 tabular-nums text-orange-700">{row.ot_hours > 0 ? fmtHours(row.ot_hours) : '–'}</td>
                  {isAdmin && <td className="text-right px-3 py-2 tabular-nums text-gray-900">{fmtMoney(row.labor_cost)}</td>}
                </tr>
              ))
            )}
          </tbody>
          {(timesheet?.rows || []).length > 0 && (
            <tfoot className="bg-gray-50 border-t border-gray-200">
              <tr>
                <td className="px-3 py-2 font-semibold text-gray-700">Total</td>
                {DAY_LABELS.map((_, i) => {
                  const sum = (timesheet?.rows || []).reduce((s, r) => s + (r.days[i] || 0), 0);
                  return (
                    <td key={i} className="text-right px-2 py-2 tabular-nums text-gray-700">{sum ? fmtHours(sum) : '–'}</td>
                  );
                })}
                <td className="text-right px-3 py-2 font-semibold text-gray-900 tabular-nums">{fmtHours(totalHours)}</td>
                <td className="text-right px-3 py-2 tabular-nums text-orange-700">
                  {fmtHours((timesheet?.rows || []).reduce((s, r) => s + (r.ot_hours || 0), 0))}
                </td>
                {isAdmin && <td className="text-right px-3 py-2 font-semibold text-gray-900 tabular-nums">{fmtMoney(totalLabor)}</td>}
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Entries list (admins only) */}
      {isAdmin && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-4 py-3 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Entries this week</h2>
            <p className="text-xs text-gray-500">Edit clock-in/out times or delete bad entries.</p>
          </div>
          <div className="divide-y divide-gray-100">
            {entries.length === 0 ? (
              <div className="px-4 py-6 text-center text-gray-500 text-sm">No entries.</div>
            ) : (
              entries.map((e) => (
                <div key={e.id} className="px-4 py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-900">
                      {e.member?.display_name || 'Unnamed'}
                      {e.is_open && <span className="ml-2 text-xs text-green-700 bg-green-100 px-2 py-0.5 rounded">open</span>}
                    </div>
                    <div className="text-xs text-gray-500">
                      {new Date(e.clock_in).toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                      <OnSiteBadge status={e.clock_in_on_site} hasCoords={e.clock_in_lat != null} label="in" />
                      {' → '}
                      {e.clock_out
                        ? new Date(e.clock_out).toLocaleString([], { hour: 'numeric', minute: '2-digit' })
                        : '—'}
                      {e.clock_out && <OnSiteBadge status={e.clock_out_on_site} hasCoords={e.clock_out_lat != null} label="out" />}
                      {e.hours != null && <span className="ml-2 text-gray-700">({fmtHours(e.hours)} hr)</span>}
                    </div>
                    {e.note && <div className="text-xs text-gray-400 italic mt-0.5">{e.note}</div>}
                  </div>
                  <div className="flex gap-3 flex-shrink-0">
                    <button onClick={() => openEdit(e)} className="text-blue-600 hover:text-blue-800 text-sm font-medium">Edit</button>
                    <button onClick={() => handleDelete(e)} className="text-red-600 hover:text-red-800 text-sm font-medium">Delete</button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Manual entry / edit modal */}
      {showForm && formEntry && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">
              {formEntry.id ? 'Edit time entry' : 'Add manual entry'}
            </h2>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Employee</label>
              <select
                value={formEntry.account_member_id}
                onChange={(e) => setFormEntry({ ...formEntry, account_member_id: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {members.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.user?.display_name || 'Unnamed'} ({m.role})
                  </option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Clock in</label>
                <input
                  type="datetime-local"
                  value={formEntry.clock_in}
                  onChange={(e) => setFormEntry({ ...formEntry, clock_in: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Clock out</label>
                <input
                  type="datetime-local"
                  value={formEntry.clock_out}
                  onChange={(e) => setFormEntry({ ...formEntry, clock_out: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Note (optional)</label>
              <input
                type="text"
                value={formEntry.note}
                onChange={(e) => setFormEntry({ ...formEntry, note: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Forgot to clock out, etc."
              />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={closeForm}
                className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={saveForm}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// GPS on-site indicator for a single clock-in or clock-out. Colored dot with
// a tooltip so the row stays scannable and admins can hover for detail.
const OnSiteBadge = ({ status, hasCoords, label }) => {
  if (!hasCoords) {
    return (
      <span
        title={`No location captured at clock-${label} — employee may have denied GPS permission`}
        className="ml-1 inline-block w-2 h-2 rounded-full bg-gray-300 align-middle"
      />
    );
  }
  if (status === true) {
    return (
      <span
        title={`On site at clock-${label}`}
        className="ml-1 inline-block w-2 h-2 rounded-full bg-green-500 align-middle"
      />
    );
  }
  if (status === false) {
    return (
      <span
        title={`Off site at clock-${label} — GPS says employee was not at the wash`}
        className="ml-1 inline-block w-2 h-2 rounded-full bg-red-500 align-middle"
      />
    );
  }
  // status === null → have coords but the wash has no lat/lon set yet.
  return (
    <span
      title={`Location captured at clock-${label}, but the wash coordinates aren't set (Settings → GPS Location)`}
      className="ml-1 inline-block w-2 h-2 rounded-full bg-yellow-400 align-middle"
    />
  );
};

const Tile = ({ label, value }) => (
  <div className="bg-white rounded-lg shadow p-4">
    <div className="text-xs text-gray-500 uppercase tracking-wide">{label}</div>
    <div className="text-2xl font-bold text-gray-900 mt-1 tabular-nums">{value}</div>
  </div>
);

export default Timesheet;
